import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import { createPinia } from 'pinia'
import { initLocale } from '@/composables/useLocaleInit'
import { i18n } from './i18n'
import { useDarkModeStore } from '@/stores/darkModeStore'
import { createHead } from '@unhead/vue/client'
import { VueReCaptcha } from 'vue-recaptcha-v3'

// CSS imports
import './assets/css/tailwind.css'
import './assets/css/iti.css'
import './assets/css/styles.css'

// PrimeVue imports
import PrimeVue from 'primevue/config'
import Aura from '@primeuix/themes/aura'
import 'primeicons/primeicons.css'

// PrimeVue components import
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Message from 'primevue/message'
import Card from 'primevue/card'
import Select from 'primevue/select'

// PrimeVue directives import
import Ripple from 'primevue/ripple'
import Tooltip from 'primevue/tooltip'
import Fluid from 'primevue/fluid'

// PrimeVue services import
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import DialogService from 'primevue/dialogservice'

// Custom components import
import FormGroup from '@/components/admin/form/FormGroup.vue'
import { useAuthStore } from '@/stores/authStore'

const app = createApp(App)

const pinia = createPinia()
const head = createHead()
app.use(pinia)
app.use(head)
app.use(i18n)
initLocale().catch(() => {})
app.use(VueReCaptcha, {
  siteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
  loaderOptions: { autoHideBadge: true },
})

window.addEventListener('storage', (event) => {
  if (event.key !== 'authToken') return

  const auth = useAuthStore()

  if (event.newValue === null && auth.token) {
    auth.clearAuthData()
    if (router.currentRoute.value.path.startsWith('/admin')) {
      router.replace({ name: 'LoginPage' })
    }
  }

  if (event.newValue && event.newValue !== auth.token) {
    auth.hydrate()
  }
})

document.addEventListener('visibilitychange', async () => {
  if (!document.hidden) {
    const auth = useAuthStore()
    if (auth.token && router.currentRoute.value.path.startsWith('/admin')) {
      auth.fetchMe().catch(() => {})
    }
  }
})

const darkModeStore = useDarkModeStore()
darkModeStore.applyTheme()

app.use(router)
  .use(PrimeVue, {
    ripple: true,
    inputStyle: 'outlined',
    theme: {
      preset: Aura,
      options: {
        darkModeSelector: '.dark'
      }
    },
    locale: {
      firstDayOfWeek: 1
    }
  })
  .use(ToastService)
  .use(ConfirmationService)
  .use(DialogService)

app.component('InputText', InputText)
  .component('Textarea', Textarea)
  .component('Button', Button)
  .component('Message', Message)
  .component('Card', Card)
  .component('Select', Select)

app.directive('Ripple', Ripple)
  .directive('Tooltip', Tooltip)
  .directive('Fluid', Fluid)

app.component('FormGroup', FormGroup)

app.mount('#app')